/**
 * TimeSlotCell Component
 *
 * Individual timeslot cell with status-based styling for the scheduling grid.
 * Displays time in HH:mm format and provides visual feedback for different states.
 *
 * Requirements: 1.3, 1.4, 1.5, 1.6, 6.1, 6.2, 6.3
 */

import React from 'react'
import styled from 'styled-components'
import { TimeSlot, SlotStatus } from '../types'
import { COLORS, FONTS, TRANSITIONS, HOVER_SCALE, FOCUS_RING_WIDTH } from '../utils/theme'

/**
 * Props for the TimeSlotCell component
 */
export interface TimeSlotCellProps {
  /** The timeslot data to display */
  slot: TimeSlot
  /** Whether this slot is currently selected */
  isSelected: boolean
  /** Whether this slot is highlighted during drag selection */
  isHighlighted: boolean
  /** Click handler for the slot */
  onClick: (event: React.MouseEvent) => void
}

/**
 * Styled container for the timeslot cell
 * Implements status-based styling, hover effects, and accessibility features
 */
const SlotContainer = styled.div<{
  status: SlotStatus
  isSelected: boolean
  isHighlighted: boolean
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  border-radius: 20px;
  font-family: ${FONTS.primary};
  cursor: ${({ status }) => 
    status === 'booked' ? 'pointer' : 'pointer'};
  transition: all ${TRANSITIONS.default};
  user-select: none;

  /* Base styling by status */
  background-color: ${({ status, isSelected, isHighlighted }) => {
    if (isSelected) return COLORS.primary;
    if (isHighlighted) return COLORS.primaryLight;
    if (status === 'blocked') return COLORS.statusBlocked;
    if (status === 'booked') return COLORS.statusBooked;
    return COLORS.background;
  }};

  color: ${({ status, isSelected }) => {
    if (isSelected) return '#FFFFFF';
    if (status === 'blocked' || status === 'booked') return '#FFFFFF';
    return COLORS.text;
  }};

  border: 2px solid ${({ status, isSelected, isHighlighted }) => {
    if (isSelected) return COLORS.primary;
    if (isHighlighted) return COLORS.primary;
    if (status === 'open') return COLORS.statusOpen;
    return 'transparent';
  }};

  /* Hover effects for open slots only */
  &:hover {
    ${({ status, isSelected }) =>
      status === 'open' && !isSelected &&
      `
      transform: scale(${HOVER_SCALE});
      border-color: ${COLORS.primary};
      box-shadow: 0 2px 8px rgba(235, 124, 12, 0.2);
    `}
  }

  /* Focus state for keyboard navigation */
  &:focus {
    outline: ${FOCUS_RING_WIDTH} solid ${COLORS.focusRing};
    outline-offset: 2px;
  }

  /* Reduced motion support */
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover {
      transform: none;
    }
  }
`

/**
 * Formats a Date object to HH:mm format
 * @param date - The date to format
 * @returns Time string in HH:mm format (e.g., "09:30", "14:00")
 */
export function formatTimeHHmm(date: Date): string {
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  return `${hours}:${minutes}`
}

/**
 * TimeSlotCell Component
 *
 * Renders an individual timeslot with status-based styling and interaction handling.
 * Uses data attributes for drag selection integration.
 *
 * @param props - Component props
 * @returns React element representing the timeslot cell
 */
const TimeSlotCell: React.FC<TimeSlotCellProps> = ({
  slot,
  isSelected,
  isHighlighted,
  onClick,
}) => {
  const formattedTime = formatTimeHHmm(slot.time)

  return (
    <SlotContainer
      status={slot.status}
      isSelected={isSelected}
      isHighlighted={isHighlighted}
      onClick={onClick}
      data-slot-id={slot.id}
      data-slot-status={slot.status}
      role="button"
      tabIndex={slot.status === 'blocked' ? -1 : 0}
      aria-label={`Time slot ${formattedTime}, status: ${slot.status}${isSelected ? ', selected' : ''}`}
      aria-pressed={isSelected}
    >
      {formattedTime}
    </SlotContainer>
  )
}

/**
 * Custom comparison function for React.memo
 * Prevents unnecessary re-renders by comparing only the props that affect rendering.
 * Compares slot.id, slot.status, isSelected, and isHighlighted.
 *
 * Requirements: 5.6 - Implement React.memo with custom comparison for Grid components
 */
function arePropsEqual(prevProps: TimeSlotCellProps, nextProps: TimeSlotCellProps): boolean {
  return (
    prevProps.slot.id === nextProps.slot.id &&
    prevProps.slot.status === nextProps.slot.status &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isHighlighted === nextProps.isHighlighted
  )
}

export default React.memo(TimeSlotCell, arePropsEqual)
